'use client';

import useConfig from '@/hooks/useConfig';
import { useWidgetStore } from '@/providers/widget.provider';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  VpButton,
  VpFormControl,
  VpFormError,
  VpFormHelper,
  VpFormLabel,
  VpInput,
  VpSelect,
  VpSelectOption,
  VpTextarea,
} from '@vtmn-play/react';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { object, ObjectSchema, string } from 'yup';
import StickyFooter from '../StickyFooter/StickyFooter';
import StepContainer from '../StepContainer/StepContainer';
import { CategoryTypeEnum } from '@repo/common/enums';
import InputPhone from '@repo/ui/InputPhone';
import parsePhoneNumber, { isValidPhoneNumber } from 'libphonenumber-js';
import { useTrackingAnalytics } from '@/providers/tracking.provider';
import { TrackingEvent } from '@/interfaces/tracking.interface';
import { isVisioStoreCategory } from '@repo/common/helpers';

import './WidgetInformationsStep.scss';

const DESCRIPTION_MAX_LENGTH = 1024;

export interface WidgetInformationsStepDescription {
  label?: string;
  helper?: string;
  placeholder?: string;
}

export interface WidgetInformationsStepProps {
  category?: string;
  isPhoneRequired: boolean;
  isDescriptionRequired?: boolean;
  isOrderNumberEnabled?: boolean;
  isLeasingNumberEnabled?: boolean;
  countryCode: string;
  locale: string;
  customDescription?: WidgetInformationsStepDescription;
}

export interface WidgetInformationsStepFormValues {
  size?: string;
  description?: string;
  order?: string;
  leasing?: string;
  phone?: string;
}

const WidgetInformationsStep = ({
  category,
  isPhoneRequired,
  isDescriptionRequired = false,
  isOrderNumberEnabled,
  isLeasingNumberEnabled,
  countryCode,
  locale,
  customDescription,
}: WidgetInformationsStepProps) => {
  const t = useTranslations('Widget.pages.steps.informations.content');
  const { currentStep, goToNextStep } = useConfig();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [isSkipping, setIsSkipping] = useState<boolean>();
  const { categoryType, countryService, informations, setInformations } =
    useWidgetStore((state) => state);
  const { trackEvent } = useTrackingAnalytics();

  const isVisioStore = isVisioStoreCategory(category);
  const descriptionPlaceholder = useMemo(() => {
    if (customDescription?.placeholder) {
      return customDescription.placeholder;
    }
    if (
      category &&
      t.has(`form.fields.description.placeholder-${category?.toLowerCase()}`)
    ) {
      return t(
        `form.fields.description.placeholder-${category?.toLowerCase()}`,
      );
    }
    if (isVisioStore) {
      return '';
    }

    return t('form.fields.description.placeholder-generic');
  }, [t, customDescription, category, isVisioStore]);

  const isSizeDisplayed =
    category === CategoryTypeEnum.TEST_E_BIKE ||
    category === CategoryTypeEnum.TEST_ROAD_GRAVEL_BIKE;

  const defaultValues = useMemo(() => {
    return {
      size: isSizeDisplayed ? (informations?.size ?? '') : '',
      description: informations?.description ?? '',
      order: isOrderNumberEnabled ? (informations?.order ?? '') : undefined,
      leasing: isLeasingNumberEnabled
        ? (informations?.leasing ?? '')
        : undefined,
      phone: isPhoneRequired ? (informations?.phone ?? '') : undefined,
    };
  }, [
    informations,
    isSizeDisplayed,
    isPhoneRequired,
    isOrderNumberEnabled,
    isLeasingNumberEnabled,
  ]);

  const defaultCountry = useMemo(() => {
    if (informations?.phone) {
      const phoneNumber = parsePhoneNumber(informations.phone);
      if (phoneNumber?.country) return phoneNumber.country;
    }
    return countryCode ?? 'GB';
  }, [informations?.phone]);

  const validationSchema: ObjectSchema<WidgetInformationsStepFormValues> =
    useMemo(() => {
      const nothingFilled = (...values: Array<string | undefined>) => {
        return !values.some((value) => value !== undefined && value.length > 0);
      };

      return object().shape(
        {
          phone: isPhoneRequired
            ? string()
                .required(t('form.fields.phone.required'))
                .test(
                  'is-valid-phone',
                  t('form.fields.phone.invalid'),
                  (value) => {
                    if (!value) return false;
                    try {
                      const country = parsePhoneNumber(value)?.country || 'GB';
                      return isValidPhoneNumber(value, country);
                    } catch {
                      return false;
                    }
                  },
                )
                .transform((value) => {
                  if (!value) return '';
                  try {
                    const phoneNumber = parsePhoneNumber(value);
                    return phoneNumber
                      ? phoneNumber.formatInternational()
                      : value;
                  } catch {
                    return value;
                  }
                })
            : string(),
          order: string()
            .optional()
            .max(30, t('form.fields.order.maxLength', { maxLength: 30 }))
            .matches(/^[a-zA-Z0-9]*$/, t('form.fields.order.pattern')),
          leasing: string().optional(),
          size: string().when(['description', 'phone', 'order', 'leasing'], {
            is: (...values: Array<string | undefined>) =>
              nothingFilled(...values),
            then: (schema) => schema.required(),
            otherwise: (schema) => schema,
          }),
          description: string().when(['size', 'phone', 'order', 'leasing'], {
            is: (...values: Array<string | undefined>) =>
              isDescriptionRequired || nothingFilled(...values),
            then: (schema) =>
              schema
                .trim()
                .min(1)
                .max(
                  DESCRIPTION_MAX_LENGTH,
                  t('form.fields.description.maxLength', {
                    maxLength: DESCRIPTION_MAX_LENGTH,
                  }),
                )
                .required(),
            otherwise: (schema) => schema,
          }),
        },
        [['description', 'size']],
      );
    }, [t, isDescriptionRequired, isPhoneRequired]);

  const {
    control,
    formState: { isSubmitting, isValid, errors },
    handleSubmit,
  } = useForm<WidgetInformationsStepFormValues>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const trackInformationEvent = useCallback(() => {
    trackEvent(TrackingEvent.INFO_VALIDATED, {
      page_name: currentStep?.id,
      category_name: categoryType,
      category_id: categoryType,
      service_name: countryService?.productSheet?.name,
      service_id: countryService?.productSheet?.sku,
    });
  }, [currentStep, categoryType, countryService, trackEvent]);

  const handleSubmitOnClick = useCallback(
    async (data: WidgetInformationsStepFormValues) => {
      setIsLoading(true);
      setInformations(data);

      trackInformationEvent();

      try {
        await goToNextStep();
      } catch {
        setIsLoading(false);
      }
    },
    [goToNextStep, setInformations, setIsLoading, trackInformationEvent],
  );

  const handleSkipOnClick = useCallback(async () => {
    setIsSkipping(true);
    setInformations({
      description: '',
      size: '',
      leasing: isLeasingNumberEnabled ? '' : undefined,
      order: isOrderNumberEnabled ? '' : undefined,
    });

    trackInformationEvent();

    try {
      await goToNextStep();
    } catch {
      setIsSkipping(false);
    }
  }, [
    goToNextStep,
    isLeasingNumberEnabled,
    isOrderNumberEnabled,
    setInformations,
    setIsSkipping,
    trackInformationEvent,
  ]);

  const displaySkipButton = !isPhoneRequired && !isDescriptionRequired;
  const descriptionLabel = useMemo(() => {
    if (customDescription?.label) {
      return customDescription.label;
    }
    return isVisioStore
      ? t('form.fields.description.label-visio')
      : t('form.fields.description.label');
  }, [customDescription, isVisioStore, t]);

  const descriptionHelper = useMemo(() => {
    if (customDescription?.helper) {
      return customDescription.helper;
    }
    return isVisioStore
      ? t('form.fields.description.helper-visio', {
          maxLength: DESCRIPTION_MAX_LENGTH,
        })
      : t('form.fields.description.helper', {
          maxLength: DESCRIPTION_MAX_LENGTH,
        });
  }, [customDescription, isVisioStore, t]);

  return (
    <>
      <StepContainer>
        <form
          className='mt-1 widget-information'
          id='widget-information-form'
          onSubmit={handleSubmit(handleSubmitOnClick)}
        >
          {isPhoneRequired && (
            <Controller
              control={control}
              name='phone'
              render={({ field: { name, value, onChange, onBlur } }) => (
                <VpFormControl>
                  <VpFormLabel>{t(`form.fields.phone.label`)}</VpFormLabel>
                  <div className='relative flex is-full'>
                    <InputPhone
                      defaultCountry={defaultCountry}
                      name={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      locale={locale}
                    />
                  </div>
                  {errors.phone && (
                    <VpFormError>{errors.phone.message}</VpFormError>
                  )}
                  <VpFormHelper>{t(`form.fields.phone.helper`)}</VpFormHelper>
                </VpFormControl>
              )}
            />
          )}
          {isOrderNumberEnabled && (
            <Controller
              control={control}
              name='order'
              render={({ field: { name, value, onChange, onBlur } }) => (
                <VpFormControl>
                  <VpFormLabel>{t(`form.fields.order.label`)}</VpFormLabel>
                  <VpInput
                    id={`${name}-input`}
                    name={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                  {errors.order && (
                    <VpFormError>{errors.order.message}</VpFormError>
                  )}
                  <VpFormHelper>{t(`form.fields.order.helper`)}</VpFormHelper>
                </VpFormControl>
              )}
            />
          )}
          {isLeasingNumberEnabled && (
            <Controller
              control={control}
              name='leasing'
              render={({ field: { name, value, onChange, onBlur } }) => (
                <VpFormControl>
                  <VpFormLabel>{t(`form.fields.leasing.label`)}</VpFormLabel>
                  <VpInput
                    id={`${name}-input`}
                    name={name}
                    placeholder={t('form.fields.leasing.placeholder')}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                  {errors.leasing && (
                    <VpFormError>{errors.leasing.message}</VpFormError>
                  )}
                  <VpFormHelper>{t('form.fields.leasing.helper')}</VpFormHelper>
                </VpFormControl>
              )}
            />
          )}
          {isSizeDisplayed && (
            <Controller
              control={control}
              name='size'
              render={({ field: { name, value, onChange, onBlur } }) => (
                <VpFormControl disabled={isSubmitting || isLoading}>
                  <VpFormLabel>{t(`form.fields.size.label`)}</VpFormLabel>
                  <VpSelect
                    id={`${name}-select`}
                    name={name}
                    placeholder={t(`form.fields.size.placeholder`)}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  >
                    <VpSelectOption value='S/M'>
                      {t(`form.fields.size.options.s-m`)}
                    </VpSelectOption>
                    <VpSelectOption value='L/XL'>
                      {t(`form.fields.size.options.l-xl`)}
                    </VpSelectOption>
                  </VpSelect>
                </VpFormControl>
              )}
            />
          )}
          <Controller
            control={control}
            name='description'
            render={({
              field: { ref, name, value, onChange, onBlur },
              fieldState: { error },
            }) => {
              return (
                <VpFormControl
                  className='form-content'
                  disabled={isSubmitting || isLoading}
                  status={error ? 'error' : undefined}
                >
                  <VpFormLabel>{descriptionLabel}</VpFormLabel>
                  <VpTextarea
                    id={`${name}-textarea`}
                    name={name}
                    onBlur={onBlur}
                    onChange={onChange}
                    placeholder={descriptionPlaceholder}
                    ref={ref}
                    rows={5}
                    value={value}
                  />
                  {error?.message ? (
                    <VpFormError>{descriptionHelper}</VpFormError>
                  ) : (
                    <VpFormHelper className='text-content-quiet'>
                      {descriptionHelper}
                    </VpFormHelper>
                  )}
                </VpFormControl>
              );
            }}
          />
        </form>
      </StepContainer>
      <StickyFooter>
        <VpButton
          className='widget-information-button'
          disabled={isSubmitting || isLoading || !isValid || isSkipping}
          form='widget-information-form'
          loading={isSubmitting || isLoading}
          shape='squared'
          size='large'
          type='submit'
        >
          {t('form.buttons.submit')}
        </VpButton>
        {displaySkipButton && (
          <VpButton
            className='widget-information-button'
            disabled={isSubmitting || isLoading || isSkipping}
            shape='squared'
            variant='tertiary'
            size='large'
            onClick={handleSkipOnClick}
          >
            {t('form.buttons.skip')}
          </VpButton>
        )}
      </StickyFooter>
    </>
  );
};

export default WidgetInformationsStep;
